import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAccessibility } from '../../hooks/useAccessibility'

interface OfflineFallbackProps {
  children: React.ReactNode
}

export const OfflineFallback: React.FC<OfflineFallbackProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [wasOffline, setWasOffline] = useState(false)
  const { announce } = useAccessibility()

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      if (wasOffline) {
        announce('Connection restored. You are back online.', 'assertive')
        setWasOffline(false)
      }
    }

    const handleOffline = () => {
      setIsOnline(false)
      setWasOffline(true)
      announce('Connection lost. You are now offline. Some features may not be available.', 'assertive')
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [announce, wasOffline])

  if (!isOnline) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl w-full"
        >
          <div className="bg-white rounded-2xl shadow-xl border border-white/50 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-8 py-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    role="img"
                    aria-label="Offline icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 5.636l-12.728 12.728m0-12.728l12.728 12.728M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">You're Offline</h1>
                  <p className="text-orange-100 mt-1">
                    No internet connection detected
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                It looks like you've lost your internet connection. Some features may not be available while you're offline.
              </p>

              {/* Offline Features */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  What you can still do:
                </h2>
                <ul className="space-y-2" role="list">
                  <li className="flex items-start space-x-3" role="listitem">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600">Browse previously loaded content</span>
                  </li>
                  <li className="flex items-start space-x-3" role="listitem">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600">View cached service information</span>
                  </li>
                  <li className="flex items-start space-x-3" role="listitem">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600">Access offline documentation</span>
                  </li>
                </ul>
              </div>

              {/* Troubleshooting */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  To get back online:
                </h2>
                <ul className="space-y-2" role="list">
                  <li className="flex items-start space-x-3" role="listitem">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600">Check your Wi-Fi or mobile data connection</span>
                  </li>
                  <li className="flex items-start space-x-3" role="listitem">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600">Try moving to an area with better signal</span>
                  </li>
                  <li className="flex items-start space-x-3" role="listitem">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600">Restart your router or mobile device</span>
                  </li>
                  <li className="flex items-start space-x-3" role="listitem">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600">Contact your internet service provider</span>
                  </li>
                </ul>
              </div>

              {/* Status Indicator */}
              <div className="flex items-center justify-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse"></div>
                  <span className="text-amber-700 font-medium">
                    Checking connection...
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-8 py-4 border-t border-gray-100">
              <p className="text-sm text-gray-500 text-center">
                This page will automatically refresh when your connection is restored.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  return <>{children}</>
}